import React, { useCallback, useEffect, useState } from "react";
import {
  Monitor, Smartphone, Tablet, MapPin, LogOut, ShieldCheck,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { timeAgoIST, formatToReadableDateTime } from "../../utils/common";
import {
  AppBadge, AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  Callout, ConfirmModal,
} from "../ui";
import Shimmer from "../Common/Shimmer";

/* ---------- helpers ---------- */
const deviceIcon = (type) => {
  if (type === "mobile") return <Smartphone size={20} />;
  if (type === "tablet") return <Tablet size={20} />;
  return <Monitor size={20} />;
};

const deviceLabel = (d = {}) => {
  const browser = d.browser || "Unknown browser";
  const os = d.os || "Unknown OS";
  return `${browser} on ${os}`;
};

const locationLabel = (loc = {}) => {
  const parts = [loc.city, loc.region, loc.country].filter(Boolean);
  return parts.length ? parts.join(", ") : "Unknown location";
};

/* ========== component ========== */
const ActiveSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });

  // Confirm dialog state — { mode: 'one' | 'others', session? }
  const [confirm, setConfirm] = useState(null);
  const [working, setWorking] = useState(false);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/auth/sessions");
      setSessions(data.sessions || []);
    } catch {
      setMsg({ type: "error", text: "Failed to load active sessions." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleConfirm = async () => {
    if (!confirm) return;
    setWorking(true);
    setMsg({ type: "", text: "" });
    try {
      if (confirm.mode === "others") {
        const { data } = await apiClient.post("/auth/sessions/revoke-others");
        setMsg({ type: "success", text: data.message || "Other devices signed out." });
      } else {
        const { data } = await apiClient.delete(`/auth/sessions/${confirm.session.id}`);
        setMsg({ type: "success", text: data.message || "Device signed out." });
      }
      setConfirm(null);
      await fetchSessions();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Action failed. Please try again." });
    } finally {
      setWorking(false);
    }
  };

  const otherCount = sessions.filter((s) => !s.current).length;

  return (
    <AppCard>
      <AppCardHeader className="tw-flex-row tw-items-center tw-justify-between">
        <AppCardTitle className="[&_svg]:tw-text-primary">
          <ShieldCheck size={18} /> Active Sessions
        </AppCardTitle>
        {otherCount > 0 && (
          <AppButton
            variant="outline"
            size="sm"
            onClick={() => setConfirm({ mode: "others" })}
          >
            <LogOut size={14} /> Log out all other devices
          </AppButton>
        )}
      </AppCardHeader>

      <AppCardContent>
        <p className="tw-mb-4 tw-mt-0 tw-text-sm tw-leading-normal tw-text-content-muted">
          These are the devices currently signed in to your account. If you don't
          recognise one, sign it out — that device will be logged out on its next action.
        </p>

        {msg.text && (
          <Callout
            tone={msg.type === "success" ? "success" : "danger"}
            role="status"
            className="tw-mb-4"
          >
            {msg.text}
          </Callout>
        )}

        {loading ? (
          <div className="tw-flex tw-flex-col tw-gap-2">
            <Shimmer height={64} count={3} />
          </div>
        ) : sessions.length === 0 ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-px-4 tw-py-8 tw-text-center tw-text-content-subtle [&>p]:tw-m-0 [&>p]:tw-text-sm">
            <Monitor size={28} />
            <p>No active sessions found.</p>
          </div>
        ) : (
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
            {sessions.map((s) => (
              <li
                key={s.id}
                className={
                  "tw-flex tw-flex-wrap tw-items-start tw-gap-3.5 tw-rounded tw-border tw-border-solid tw-px-4 tw-py-3.5 tw-transition-colors min-[601px]:tw-flex-nowrap " +
                  (s.current
                    ? "tw-border-primary tw-bg-primary/[0.06]"
                    : "tw-border-line-soft tw-bg-surface-sunken hover:tw-bg-surface-hover")
                }
              >
                <span
                  className={
                    "tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-md " +
                    (s.current ? "tw-bg-primary/[0.12] tw-text-primary" : "tw-bg-surface-hover tw-text-content-muted")
                  }
                >
                  {deviceIcon(s.device?.type)}
                </span>

                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-[0.9rem] tw-font-semibold tw-text-content">
                    {deviceLabel(s.device)}
                    {s.current && <AppBadge color="emerald" size="sm" className="tw-rounded-full tw-uppercase tw-tracking-[0.02em]">This device</AppBadge>}
                    {s.platform === "mobile" && <AppBadge variant="secondary" size="sm" className="tw-rounded-full tw-uppercase tw-tracking-[0.02em]">Mobile app</AppBadge>}
                  </div>
                  <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-text-[0.8rem] tw-text-content-muted [&>span]:tw-inline-flex [&>span]:tw-items-center [&>span]:tw-gap-1">
                    <span><MapPin size={13} /> {locationLabel(s.location)}</span>
                    {s.ip && <span className="tw-text-content-subtle">· {s.ip}</span>}
                  </div>
                  <div className="tw-mt-1 tw-text-xs tw-text-content-subtle" title={`Signed in ${formatToReadableDateTime(s.createdAt)}`}>
                    {s.current ? "Active now" : `Last active ${timeAgoIST(s.lastSeenAt)}`}
                    {" · Signed in "}{formatToReadableDateTime(s.createdAt)}
                  </div>
                </div>

                <div className="tw-shrink-0 tw-self-center max-[600px]:tw-mt-1 max-[600px]:tw-w-full max-[600px]:tw-self-stretch max-[600px]:tw-text-right">
                  {s.current ? (
                    <span className="tw-whitespace-nowrap tw-text-xs tw-font-semibold tw-text-primary">Current session</span>
                  ) : (
                    <AppButton
                      variant="ghost-destructive"
                      size="sm"
                      onClick={() => setConfirm({ mode: "one", session: s })}
                    >
                      <LogOut size={14} /> Sign out
                    </AppButton>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </AppCardContent>

      <ConfirmModal
        show={!!confirm}
        onHide={() => !working && setConfirm(null)}
        onConfirm={handleConfirm}
        loading={working}
        variant="warning"
        title={confirm?.mode === "others" ? "Log out all other devices?" : "Sign out this device?"}
        message={
          confirm?.mode === "others"
            ? `This will sign out ${otherCount} other device${otherCount !== 1 ? "s" : ""}. They will need to log in again.`
            : confirm?.session
              ? `${deviceLabel(confirm.session.device)} (${locationLabel(confirm.session.location)}) will be logged out on its next action.`
              : ""
        }
        confirmText={confirm?.mode === "others" ? "Log out others" : "Sign out"}
        icon={<LogOut size={22} />}
      />
    </AppCard>
  );
};

export default ActiveSessions;
